import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertOptions } from 'sweetalert2';

export interface DocConfirmOptions {
  message?: string;
  header?: string;
  icon?: string;
  /** Kept for PrimeNG API compatibility; ignored by Swal. */
  key?: string;
  acceptLabel?: string;
  rejectLabel?: string;
  acceptButtonStyleClass?: string;
  rejectButtonStyleClass?: string;
  accept?: () => void;
  reject?: () => void;
  defaultFocus?: 'accept' | 'reject';
}

@Injectable({ providedIn: 'root' })
export class DocConfirmService {
  confirm(options: DocConfirmOptions): void {
    void this.showConfirm(options);
  }

  confirmAsync(options: DocConfirmOptions): Promise<boolean> {
    return this.showConfirm(options);
  }

  private showConfirm(options: DocConfirmOptions): Promise<boolean> {
    const isDanger = options.acceptButtonStyleClass?.includes('danger');
    const confirmText = options.acceptLabel || 'Yes';
    const cancelText = options.rejectLabel || 'No';
    const customIconHtml = this.buildCustomIconHtml(options.icon, isDanger);
    const isRtl =
      typeof document !== 'undefined' &&
      document.documentElement.getAttribute('dir') === 'rtl';

    const swalOptions: SweetAlertOptions = {
      title: options.header || '',
      text: options.message,
      showCancelButton: true,
      showDenyButton: false,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true,
      focusCancel: options.defaultFocus === 'reject',
      buttonsStyling: false,
      heightAuto: false,
      returnFocus: false,
      didOpen: (popup) => {
        const dir = isRtl ? 'rtl' : 'ltr';
        popup.setAttribute('dir', dir);
        popup.parentElement?.setAttribute('dir', dir);
      },
      customClass: {
        container: 'doc-confirm-swal',
        popup: 'doc-confirm-popup',
        confirmButton: isDanger ? 'reject-btn' : 'swal2-confirm-primary',
        cancelButton: 'accept-btn',
        denyButton: 'doc-confirm-deny-hidden',
        actions: 'doc-confirm-actions',
        icon: customIconHtml ? 'doc-confirm-icon doc-confirm-icon--custom' : 'doc-confirm-icon',
        title: 'doc-confirm-title',
        htmlContainer: 'doc-confirm-message',
      },
    };

    if (customIconHtml) {
      swalOptions.iconHtml = customIconHtml;
      // Empty icon type still renders the icon wrapper so our HTML shows.
      swalOptions.icon = 'question';
    } else {
      swalOptions.icon = this.mapIcon(options.icon, isDanger);
    }

    return Swal.fire(swalOptions).then((result) => {
      if (result.isConfirmed) {
        options.accept?.();
        return true;
      }

      options.reject?.();
      return false;
    });
  }

  /** Use PrimeIcons when callers pass `pi ...`; otherwise fall back to Swal icons. */
  private buildCustomIconHtml(icon?: string, isDanger?: boolean): string | null {
    if (!icon || !icon.includes('pi-')) {
      return null;
    }

    const toneClass = isDanger
      ? 'doc-confirm-icon-badge--danger'
      : icon.includes('exclamation') || icon.includes('warning')
        ? 'doc-confirm-icon-badge--warning'
        : icon.includes('cloud-upload') || icon.includes('upload')
          ? 'doc-confirm-icon-badge--upload'
          : 'doc-confirm-icon-badge--primary';

    return `<span class="doc-confirm-icon-badge ${toneClass}" aria-hidden="true"><i class="${icon}"></i></span>`;
  }

  private mapIcon(icon?: string, isDanger?: boolean): SweetAlertIcon {
    if (icon?.includes('exclamation') || icon === 'warning' || isDanger) {
      return 'warning';
    }

    if (icon === 'error' || icon === 'success' || icon === 'info' || icon === 'question') {
      return icon;
    }

    return 'question';
  }
}
